import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "../Misc/Pagination";
import { SpinnyThingy } from "../Misc/SpinnyThingy";
import { DeviceNode, IndividualSensorData } from "../../types/types";
import { calculateMCValue, cleanUpDateTime, roundData } from "../../utils/utility";
import { TranslateVisualSensorType } from "../Misc/TranslateVisualSensorType";
import { useChart } from "../../contexts/ChartDataContext";
import { Requester } from "../../utils/Requester";
import PortalModalHeader from "../PortalModal/PortalModalHeader";
import PortalModalContent from "../PortalModal/PortalModalContent";
import PortalModalFooter from "../PortalModal/PortalModalFooter";
import { CloseButton } from "../PortalModal/CloseButton";
import { TSensorTypes, getUnit } from "../../types/generated_types";
import { GridTable, TableColumn } from "../Table/GridTable";
import { DateRangePicker } from "../DatePicker/DateRangePicker";

interface Props {
  node: DeviceNode,
  sensorType: TSensorTypes,
  close: () => void,
}

export function ModalSensorData({
  node,
  sensorType,
  close,
}: Props): React.ReactElement {
  const { t } = useTranslation();

  const { materialMap } = useChart();

  const [modalSensorDataSpinnyThingy, setModalSensorDataSpinnyThingy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [maxPages, setMaxPages] = useState(1);
  const [modalData, setModalData] = useState<Array<IndividualSensorData>>([]);

  const switchToPage = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    setModalSensorDataSpinnyThingy(true);
    Requester.getIndividualSensor({
      id: node.id,
      count: 10, // Number of entries to be displayed per page
      page: newPage,
      // If MC then get WME data instead, MC will be calculated from WME data
      type: sensorType === TSensorTypes.MC ? TSensorTypes.WME : sensorType,
      startDate,
      endDate,
    })
    .then(result => {
      setModalSensorDataSpinnyThingy(false);
      setModalData(result.data);
      // max calculated by backend based on "count" property provided in request
      setMaxPages(result.total_pages);
    })
  }, [node.id, sensorType, startDate, endDate])

  useEffect(() => {
    switchToPage(1);
  },
  [startDate, endDate, switchToPage]);

  const getColumns = useCallback((): TableColumn<IndividualSensorData>[] => [
    {
      key: "#",
      width: "6rem",
      getTitle: () => "#",
      getCell: data => data.page_idx,
    },
    {
      key: "value",
      getTitle: () => t("Value"),
      getCell: data => {
        const value = sensorType === TSensorTypes.MC
        ? calculateMCValue(Number.parseFloat(data.value), materialMap.get(node.local_id)!).toString()
        : data.value
        return <>
          {roundData(value, data.sensor_type, t)}
          {" "}
          {getUnit(data.sensor_type)}
        </>
      },
    },
    {
      key: "type",
      getTitle: () => t("Sensor Type"),
      getCell: data => TranslateVisualSensorType(data.sensor_type),
    },
    {
      key: "time",
      getTitle: () => t("Captured Date Time"),
      getCell: data => cleanUpDateTime(data.captured_datetime),
    },
  ], [materialMap, node.local_id, sensorType, t])


  let modalContents

  if (modalSensorDataSpinnyThingy) {
    modalContents = <div style={{padding: "3rem"}}> <SpinnyThingy /> </div>
  }
  else if (!modalData.length) {
    modalContents =
    <div style={{padding: "3rem", textAlign: "center"}}>
      <h4>{t("NO DATA")}</h4>
    </div>
  }
  else {
    modalContents = <>
      <GridTable
        data={modalData}
        columns={getColumns()}
        getRowKey={data => data.page_idx + ""}
        minColumnWidth="0"
      />
      <br/>
      <Pagination
        key="pagination"
        currentPage={currentPage}
        totalPages={maxPages}
        switchToPage={(page: number) => switchToPage(page)}
        oldestNewestVisable
      />
    </>
  }

  return <>
    <PortalModalHeader
      close={close}
      title={`${t("Sensor Data for")} ${TranslateVisualSensorType(sensorType)} ${node.sensor_name}`}
    />
    <PortalModalContent>
      {modalContents}
    </PortalModalContent>
    <PortalModalFooter>
      <div style={{width: "100%"}}>
        <DateRangePicker
          start={startDate}
          end={endDate}
          onRangeSelect={(start, end) => {
            setStartDate(start)
            setEndDate(end)
          }}
          clearable
          horizontal
        />
      </div>
      <CloseButton close={close} />
    </PortalModalFooter>
  </>
}
